// NewContactPage.tsx — „Neuer Kontakt" ohne Vorauswahl Firma/Privat (User 26.09.2026).
//
// Erst der Status (und bei „Sonstige" Bereich | Rolle), dann Firma | Privat —
// bei festlegenden Rollen abgeleitet. Sobald der Typ feststeht, erscheint das
// passende Formular; der Status wandert als Vorbelegung mit und steht dort im
// selben Baustein (`KontaktStatusSchritt`) weiter zur Bearbeitung.
//
// Ein Typwechsel im Formular (Firma ↔ Privat) nimmt NUR den Status mit — die
// übrigen Felder gehören zum jeweiligen Formular. Deshalb fragt die Seite vorher.

import * as React from "react";
import { REFINED } from "../components/risikoanalyseStep/tokens";
import { SectionCard } from "../components/forms/formPrimitives";
import { KontaktStatusSchritt, type KontaktTyp } from "../components/forms/KontaktStatusSchritt";
import { ConfirmDialog } from "../components/redesign/ConfirmDialog";
import { useAuth } from "../context/AuthContext";
import { passtZuTyp, typAusRollen } from "../data/kontaktRollen";
import { rollenAusFeld, rollenInsFeld, STATUS_SONSTIGE } from "../data/kontaktstatusSegment";
import { bezuegeAusFeld, bezuegeInsFeld } from "../components/forms/widgets/StatusBezuege";
import NewPrivateContactPage from "./NewPrivateContactPage";
import NewCompanyContactPage from "./NewCompanyContactPage";
import type { FormState } from "../utils/formUtils";

/** Die Felder, die der Status-Schritt beschreibt — sie wandern beim Typwechsel mit. */
const STATUS_FELDER = [
  "kontaktStatus",
  "persoenlich",
  "kontaktStatusRollen",
  "kontaktStatusAuch",
  "kontaktStatusBezuege",
] as const;

const nurStatus = (felder: FormState): Record<string, string> =>
  Object.fromEntries(STATUS_FELDER.map((k) => [k, String(felder[k] ?? "")]));

/**
 * Status-Felder für den neuen Typ: Rollen, die nur zum anderen Typ passen,
 * fallen weg — samt ihrem Gegenüber. Sonst legten sie den Typ gleich wieder
 * fest (User 02.10.2026: „dann wird sowieso verworfen").
 */
export function rollenFuerTyp(felder: Record<string, string>, typ: KontaktTyp): Record<string, string> {
  const rollen = rollenAusFeld(felder.kontaktStatusRollen ?? "").filter((r) => passtZuTyp(r, typ));
  const bezuege = bezuegeAusFeld(felder.kontaktStatusBezuege ?? "").filter((b) => rollen.includes(b.rolle));
  return { ...felder, kontaktStatusRollen: rollenInsFeld(rollen), kontaktStatusBezuege: bezuegeInsFeld(bezuege) };
}

export default function NewContactPage() {
  const { hasPermission } = useAuth();
  const [felder, setFelder] = React.useState<Record<string, string>>({ kontaktStatus: "Interessent" });
  const [typ, setTyp] = React.useState<KontaktTyp | null>(null);
  /** Bei jedem Typwechsel neu, damit das andere Formular frisch startet. */
  const [durchlauf, setDurchlauf] = React.useState(0);
  const [wechsel, setWechsel] = React.useState<{ nach: KontaktTyp; felder: FormState } | null>(null);

  const f = (k: string) => felder[k] ?? "";
  const set = (k: string, v: string) => setFelder((alt) => ({ ...alt, [k]: v }));

  // Festlegende Rolle → Typ steht, das Formular erscheint sofort.
  const abgeleitet =
    f("kontaktStatus") === STATUS_SONSTIGE ? typAusRollen(rollenAusFeld(f("kontaktStatusRollen"))) : null;
  const wirksamerTyp: KontaktTyp | null =
    typ ?? (abgeleitet === "person" || abgeleitet === "firma" ? abgeleitet : null);

  if (wirksamerTyp) {
    const vorbelegung = { ...felder };
    const wechselAnfragen = (nach: KontaktTyp) => (formular: FormState) => setWechsel({ nach, felder: formular });
    return (
      <>
        {wirksamerTyp === "person" ? (
          <NewPrivateContactPage key={`p-${durchlauf}`} vorbelegung={vorbelegung} onTypWechsel={wechselAnfragen("firma")} />
        ) : (
          <NewCompanyContactPage key={`f-${durchlauf}`} vorbelegung={vorbelegung} onTypWechsel={wechselAnfragen("person")} />
        )}
        <ConfirmDialog
          open={!!wechsel}
          tone="primary"
          title={wechsel?.nach === "firma" ? "Als Firma anlegen?" : "Als Privatkontakt anlegen?"}
          message="Der Status und die Rollen bleiben erhalten. Alle anderen bisher eingegebenen Angaben gehen dabei verloren."
          confirmLabel="Wechseln"
          onCancel={() => setWechsel(null)}
          onConfirm={() => {
            if (!wechsel) return;
            // Rollen, die nur zum alten Typ passen, blieben sonst stehen und
            // legten den Typ gleich wieder fest.
            setFelder(rollenFuerTyp(nurStatus(wechsel.felder), wechsel.nach));
            setTyp(wechsel.nach);
            setDurchlauf((d) => d + 1);
            setWechsel(null);
          }}
        />
      </>
    );
  }

  return (
    <div style={{ minHeight: "100%", background: REFINED.white, fontFamily: REFINED.fontUI, color: REFINED.ink, overflowY: "auto" }}>
      <div style={{ width: "100%", boxSizing: "border-box", padding: 24, display: "flex", flexDirection: "column", rowGap: 18 }}>
        <h1 style={{ fontSize: "1.7rem", fontWeight: 700, textAlign: "center", margin: "12px 0 24px" }}>Neuer Kontakt</h1>
        <SectionCard title="Kontaktstatus">
          <KontaktStatusSchritt
            f={f}
            set={set}
            typ={null}
            onTyp={(t) => {
              setFelder((alt) => rollenFuerTyp(alt, t));
              setTyp(t);
            }}
            coIntern={hasPermission("cointern:read")}
          />
        </SectionCard>
      </div>
    </div>
  );
}
